import styled, { keyframes } from 'styled-components';
import React, { useEffect, useCallback, useState, useRef } from "react";
import { Input } from 'semantic-ui-react'
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import { CSVLink } from 'react-csv';
import {
  process_feedback_comments,
  process_feedback_data,
  processCommentQuestions,
} from "../calculations";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { filter_data } from "reports/Tag/calculations";
import { Pagination, Icon } from "semantic-ui-react";
import Spark from "assets/images/UI/mdi_sparkles-outline.svg";
import Empty from "./components/EmptyFeedback";
import FeedbackTag from "./FeedbackTag";
import { useLocation } from "react-router";
import ChatModal from "./components/ChatModal";
import { MiniILIcon } from "./components/Icons";
import QuestionDropdown from "./QuestionDropdown";
import RadioButtonGroup from "./components/RadioButton";
import Filters from "./Filters";
import { SET_FILTERED_DATA } from "constants/actions";
import { useCustomDataRedirect } from "./common/useCustomDataRedirect";
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";

// Highlight occurrences of searchTerm in text
const getHighlightedText = (text, searchTerm) => {
  if (!searchTerm) return text;
  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <span key={i} style={{ backgroundColor: 'yellow' }}>{part}</span>
    ) : (
      part
    )
  );
};

const COLOR_MAP = {
  "Very Negative": {
    background: "#FDF2F3",
    color: "#EF4F61",
  },
  "Slightly Negative": {
    background: "#FEF3F4",
    color: "#F27B88",
  },
  Neutral: {
    background: "#FFFAF1",
    color: "#FFA710",
  },
  "Slightly Positive": {
    background: "#F1FAF7",
    color: "#27CDA7",
  },
  "Very Positive": {
    background: "#F1FAF7",
    color: "#1B9A7E",
  },
};

// Sentiments will be translated dynamically in the component
const getSentiments = (t) => [
  { id: "Very Negative", value: "Very Negative", name: t('veryNegative') },
  {
    id: "Slightly Negative",
    value: "Slightly Negative",
    name: t('slightlyNegative'),
  },
  { id: "Neutral", value: "Neutral", name: t('neutral') },
  {
    id: "Slightly Positive",
    value: "Slightly Positive",
    name: t('slightlyPositive'),
  },
  { id: "Very Positive", value: "Very Positive", name: t('veryPositive') },
];

const Feedback = ({ data, core_data, outcomeQ, navigation }) => {
  const t = usePageTranslations('app/audit/simple/Feedback/translations');
  const { language } = useLanguage();

  // Redirect protection for custom data from NewAnalytics
  const { hasCustomData } = useCustomDataRedirect();

  const [feedback, setFeedback] = useState();
  const location = useLocation();
  const dispatch = useDispatch();
  const [unfilteredFeedback, setUnfilteredFeedback] = useState();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [AIChatOpen, setAIChatOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayedFeedback, setDisplayedFeedback] = useState();
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUserFeedback, setSelectedUserFeedback] = useState(null);
  const pageSize = 10;
  const [selectedSentiments, setSelectedSentiments] = useState([]);
  const [orientation, setOrientation] = useState(null);
  const [search, setSearch] = useState(null);
  const [selectedQuestion, setQuestion] = useState(null);
  const isOTP = location.pathname.includes("otp");
  const [feedbackType, setFeedbackType] = useState(isOTP ? 1 : 0);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [originalData, setOriginalData] = useState([]); // chart uses this
  const [questionOrder, setQuestionOrder] = useState([]);
  const [commentQuestions, setCommentQuestions] = useState([]);
  const [hasFeedbackData, setHasFeedbackData] = useState(false);
  const csvLinkRef = useRef();

  const { selectedFilters, WhiteLabel } = useSelector(
    (state) => ({
      selectedFilters: state.audit?.new_filters,
      WhiteLabel: state.white_label?.white_label,
    }),
    shallowEqual
  );

  const orgRule = !!WhiteLabel?.rule ? Number(WhiteLabel?.rule) : 3;


  // Filter config for your "Filters" component
  const feedbackFiltersConfig = [
    {
      id: "outcome",
      title: t('outcomes'),
      data: outcomeQ?.questions,
      callback: setSelectedOutcomes,
    },
    {
      id: "sentiment",
      title: t('sentiment'),
      data: getSentiments(t),
      // Instead of setting one "sentiment",
      // we pass an array of sentiments back to the parent
      callback: (selectedArray) => {
        setSelectedSentiments(selectedArray);
      },
    },
  ];

  const filtered_data = useSelector(
    (state) => state.audit?.filtered_data,
    shallowEqual
  );

  const getFactorName = (factor_id, dimension) => {
    return core_data?.questions?.dimensions?.[dimension]?.factors?.find(
      (f) => f.id == factor_id + 1
    )?.title;
  };

  const copilot = (
    <AIButton onClick={() => setAIChatOpen(true)}>
      {t('useCultureCopilot')} <IMG2 src={Spark} />
    </AIButton>
  );

  // URL param to enable CSV download button (e.g. ?csv=1 or ?download_csv=true)
  const isCsvEnabled = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const param = params.get('csv') || params.get('download_csv');
    return param === '1' || (param || '').toLowerCase() === 'true';
  }, [location.search]);

  // Build CSV rows for all visible feedback across pages (respecting search)
  const sanitizeForCsv = useCallback((value) => {
    if (value == null) return '';
    // Replace line breaks with spaces and escape double quotes by doubling them per RFC 4180
    return String(value)
      .replace(/\r?\n|\r/g, ' ')
      .replace(/"/g, '""')
      .trim();
  }, []);

  const allVisibleFeedback = React.useMemo(() => {
    if (!feedback) return [];
    let rows = feedback;
    if (search) {
      rows = feedback.filter((item) =>
        item.feedback?.toLowerCase().includes(search.toLowerCase())
      );
    }
    return rows;
  }, [feedback, search]);

  const csvHeaders = React.useMemo(() => ([
    { label: 'Feedback', key: 'feedback' },
    { label: 'AI Feedback', key: 'ai_feedback' },
    { label: 'Sentiment', key: 'sentiment' },
  ]), []);

  const getCsvData = useCallback(() => {
    return (allVisibleFeedback || []).map((item) => ({
      feedback: sanitizeForCsv(item?.feedback || ''),
      ai_feedback: sanitizeForCsv(item?.follow_up_response || ''),
      sentiment: item?.nlp?.sentiment || '',
    }));
  }, [allVisibleFeedback, sanitizeForCsv]);

  function changedPage(e, { activePage }) {
    // console.log(activePage)
    setActivePage(activePage);
  }


  useEffect(() => {
    if (core_data) {
      // Get the order of the questions based on the survey sequence
      // The survey sequence is an array of objects that contain the question id and the type of question
      // We need to extract the type from the question label,
      // It should be the first word in the label, and it should be lowercased
      const order = core_data?.Whitelabel?.survey_sequence?.map((question) => {
        let type = question?.label?.split(" ")?.[0]?.toLowerCase();
        if (type === "audit") {
          type = "culture";
        }
        return type;
      });
      setQuestionOrder(order);
    }
  }, [core_data]);

  useEffect(() => {
    if (core_data?.questions?.comments?.length > 0 && !isOTP) {
      // If we aren't OTP use the commentQuestions found in the core_data?.questions?.comments
      const allOption = {
        id: null,
        value: null,
        name: t('allComments'),
      };

      const questionOptions = core_data?.questions?.comments?.map((q) => {
        return {
          id: q.id,
          value: q.id,
          name: q.question,
        };
      });

      setCommentQuestions([allOption, ...questionOptions]);
    }
  }, [core_data]);

  useEffect(() => {
    if (selectedConversation && unfilteredFeedback) {
      const userFeedback = unfilteredFeedback.filter(
        (item) => item.employee === selectedConversation.employee
      );

      // sort the user feedback based on the id
      userFeedback.sort((a, b) => {
        return a.id - b.id;
      });

      userFeedback.sort((a, b) => {
        const aIndex = questionOrder.indexOf(a.type.toLowerCase());
        const bIndex = questionOrder.indexOf(b.type.toLowerCase());
        return aIndex - bIndex;
      });

      setSelectedUserFeedback(userFeedback);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (feedback && feedback) {
      // When the page changes we need to update the displayed insights
      const start = (activePage - 1) * pageSize;
      const end = start + pageSize;
      let filteredFeedback = feedback;
      if (search) {
        filteredFeedback = feedback.filter((item) =>
          item.feedback?.toLowerCase().includes(search.toLowerCase())
        );
      }
      setTotalPages(Math.ceil(filteredFeedback.length / pageSize));
      setDisplayedFeedback(filteredFeedback.slice(start, end));
    }
  }, [activePage, feedback, search]);

  const getOutcomeName = (outcome_id, outcomeQ) => {
    return outcomeQ?.questions?.find((o) => Number(o.id) === Number(outcome_id))
      ?.name;
  };

  useEffect(() => {
    if (data?.[0]?.[0]) {
      if (
        !isOTP &&
        core_data?.questions?.comments?.length > 0 &&
        feedbackType === 1
      ) {
        setUnfilteredFeedback(
          processCommentQuestions(
            core_data?.questions?.comments,
            data?.[0],
            undefined,
            language
          )
        );
        return;
      }

      if (isOTP) {
        setUnfilteredFeedback(process_feedback_comments(data[0], undefined, language));
        return;
      } else {
        const feedbackData = process_feedback_data(data?.[0], undefined, true, undefined, undefined, language);
        setUnfilteredFeedback(feedbackData);
        // Check if there's actual feedback data (not just comments)
        setHasFeedbackData(feedbackData?.some(item => item.type?.toLowerCase() === "culture" || item.type?.toLowerCase() === "outcome"));
        return;
      }
    } else {
      if (
        (Array.isArray(data[0]) && data[0].length > 0) ||
        (!Array.isArray(data[0]) && data.length > 0)
      ) {
        if (
          !isOTP &&
          core_data?.questions?.comments?.length > 0 &&
          feedbackType === 1
        ) {
          setUnfilteredFeedback(
            processCommentQuestions(
              core_data?.questions?.comments,
              data,
              undefined,
              language
            )
          );
          return;
        }

        if (isOTP) {
          setUnfilteredFeedback(process_feedback_comments(data, undefined, language));
          return;
        } else {
          const feedbackData = process_feedback_data(data, undefined, true, undefined, undefined, language);
          setUnfilteredFeedback(feedbackData);
          // Check if there's actual feedback data (not just comments)
          setHasFeedbackData(feedbackData?.some(item => item.type?.toLowerCase() === "culture" || item.type?.toLowerCase() === "outcome"));
        }
      }
    }
  }, [data, feedbackType]);

  useEffect(() => {
    if (
      feedback?.length === 0 &&
      !isOTP &&
      core_data?.questions?.comments?.length > 0
    ) {
      setFeedbackType(1);
    }
  }, [feedback, isOTP, core_data]);

  useEffect(() => {
    let filteredData;
    let feedbackData;

    if (data?.[0]?.[0]) {
      filteredData = filter_data(data[0], selectedFilters);
      dispatch({
        type: SET_FILTERED_DATA,
        payload: filteredData,
      });
    } else {
      filteredData = filter_data(data, selectedFilters);
    }

    const skipOutcomes =
      selectedFilters?.filter((filter) => filter.type_of === "factor")?.length >
        0 && selectedOutcomes.length === 0;
    const hasFactorsSelected =
      selectedFilters?.filter((f) => f.type_of === "factor")?.length > 0;

    // If there are factors selected, we need to make sure they don't get filtered out
    // To prevent this, we will create a separate array of the filtered factor feedback, and then merge
    // it with the filtered data in the end IF hasFactorsSelected is true
    let factorFeedbackData = [];
    if (hasFactorsSelected) {
      factorFeedbackData = process_feedback_data(
        filteredData,
          {
            "selected1": null,
            "selected2": null,
            "selected3": null
        },
        false,
        false
      )?.filter((item) => item.type?.toLowerCase() === "culture");
    }

    if (
      !isOTP &&
      core_data?.questions?.comments?.length > 0 &&
      feedbackType === 1
    ) {
      feedbackData = processCommentQuestions(
        core_data?.questions?.comments,
        filteredData,
        {
          "selected1": null,
          "selected2": null,
          "selected3": null
        },
        language
      );
      if (selectedQuestion) {
        feedbackData = feedbackData.filter(
          (item) => item.id === selectedQuestion
        );
      }

      // Set originalData BEFORE sentiment filtering (for chart)
      setOriginalData(feedbackData?.filter((item) => item.feedback));

      // Apply sentiment filtering for comments (for displayed list only)
      if (selectedSentiments?.length > 0) {
        feedbackData = feedbackData.filter((item) =>
          selectedSentiments.includes(item?.nlp?.sentiment)
        );
      }
      setFeedback(feedbackData?.filter((item) => item.feedback));
    } else if (isOTP) {
      feedbackData = process_feedback_comments(filteredData, {
        "selected1": null,
        "selected2": null,
        "selected3": null
      }, language);

      if (selectedQuestion) {
        feedbackData = feedbackData.filter(
          (item) => item.id === selectedQuestion
        );
      }

      // Set originalData BEFORE sentiment filtering (for chart)
      setOriginalData(feedbackData?.filter((item) => item.feedback));

      // Apply sentiment filtering for OTP comments (for displayed list only)
      if (selectedSentiments?.length > 0) {
        feedbackData = feedbackData.filter((item) =>
          selectedSentiments.includes(item?.nlp?.sentiment)
        );
      }

      setFeedback(feedbackData?.filter((item) => item.feedback));
    } else if (selectedOutcomes.length > 0) {
      feedbackData = process_feedback_data(
        filteredData,
        {
          "selected1": null,
          "selected2": null,
          "selected3": null
      },
        false,
        selectedOutcomes,
        hasFactorsSelected,
        language
      );

      if (skipOutcomes) {
        feedbackData = feedbackData.filter(
          (item) => item.type?.toLowerCase() !== "outcome"
        );
      }

      if (hasFactorsSelected && !skipOutcomes) {
        feedbackData = feedbackData.concat(factorFeedbackData);
      }

      setOriginalData(feedbackData?.filter((item) => item.feedback));
      setFeedback(feedbackData?.filter((item) => item.feedback));
    } else {
      if (selectedSentiments?.length > 0) {
        feedbackData = process_feedback_data(
          filteredData,
          {
            "selected1": null,
            "selected2": null,
            "selected3": null
        },
          false,
          selectedOutcomes,
          hasFactorsSelected,
          language
        );
        if (selectedSentiments?.length > 0) {
          feedbackData = feedbackData.filter((item) =>
            selectedSentiments.includes(item?.nlp?.sentiment)
          );
        }
      } else {
        // If there are no filters, just process the data,
        feedbackData = process_feedback_data(filteredData, {
          "selected1": null,
          "selected2": null,
          "selected3": null
        }, true, undefined, undefined, language);

        setOriginalData(feedbackData?.filter((item) => item.feedback));
      }

      if (skipOutcomes) {
        feedbackData = feedbackData.filter(
          (item) => item.type?.toLowerCase() !== "outcome"
        );
      }

      if (hasFactorsSelected && !skipOutcomes) {
        feedbackData = feedbackData.concat(factorFeedbackData);
      }

      setFeedback(feedbackData?.filter((item) => item.feedback));
    }

    // Reset page to 1 when filtering and recalculate total pages
    setActivePage(1);
    setTotalPages(Math.ceil(feedbackData.length / pageSize));
  }, [
    navigation,
    data,
    selectedFilters,
    selectedSentiments,
    orientation,
    selectedQuestion,
    feedbackType,
    selectedOutcomes,
  ]);

  const getColor = (type) => {
    const colors = {
      "Very Negative": "#de2121",
      "Slightly Negative": "#de2121",
      Neutral: "#f7b531",
      "Slightly Positive": "#33b054",
      "Very Positive": "#33b054",
    };

    return colors[type];
  };

  // Translate sentiment value to display name
  const getSentimentDisplayName = (sentimentValue) => {
    const sentimentMap = {
      "Very Negative": t('veryNegative'),
      "Slightly Negative": t('slightlyNegative'),
      "Neutral": t('neutral'),
      "Slightly Positive": t('slightlyPositive'),
      "Very Positive": t('veryPositive'),
    };
    return sentimentMap[sentimentValue] || sentimentValue;
  };


  if (core_data && core_data.noData && !hasCustomData) {
    return (
      <Container>
        <Empty />
      </Container>
    );
  }

  // Example: toggled from "selectedFilter" to "selectedSentiments" usage
  const handleSentimentFilter = (newSentimentArray) => {
    setSelectedSentiments(newSentimentArray || []);
  };

  const displayEmptyPage = () => {
    // If the displayedFeedback length is 0 and there is no selected filters of any sort
    // return the empty page
    if (
      displayedFeedback?.length === 0 &&
      selectedSentiments?.length === 0 &&
      selectedFilters?.length === 0 &&
      selectedOutcomes?.length === 0 &&
      !search
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Container>
        <AnalyticsBreadcrumb currentComponent="feedback" />
        {/* <CultureLabel>Culture Analytics</CultureLabel> */}

        <TopTitle>
          <TitleIconWrapper aria-hidden>
            <FeedbackIcon />
          </TitleIconWrapper>
          {t('comments')}
        </TopTitle>
        <TopDescription>
          {t('description')}
        </TopDescription>
        {isCsvEnabled && (feedback?.length || 0) > 0 && (
          <ActionRow>
            <DownloadButton onClick={() => csvLinkRef?.current?.link?.click()}>
              {t('downloadCsv')}
            </DownloadButton>
            <CSVLink
              data={getCsvData()}
              headers={csvHeaders}
              filename={"feedback-export.csv"}
              ref={csvLinkRef}
              separator="," 
              enclosingCharacter={'"'}
              uFEFF={true}
              style={{ display: 'none' }}
            />
          </ActionRow>
        )}
        {!displayEmptyPage() && (
          <>
            <FeedbackTag
              selectedQuestion={selectedQuestion}
              setQuestion={setQuestion}
              outcomeQ={outcomeQ}
              data={(originalData?.length > 0 && originalData) || feedback}
              setSelectedOutcomes={(selectedArray) => {
                // Filters component provides the full selected outcomes array.
                // Accept it directly to avoid accidental nesting.
                setSelectedOutcomes(Array.isArray(selectedArray) ? selectedArray : []);
              }}
              // Pass an array instead of a single string
              selectedSentiments={selectedSentiments}
              setSelectedSentiments={handleSentimentFilter}
              copilot={copilot}
              isOTP={isOTP}
              showComments={core_data?.questions?.comments?.length > 0 && hasFeedbackData}
              setFeedbackType={setFeedbackType}
              feedbackType={isOTP ? 1 : feedbackType}
              displayedFeedback={displayedFeedback}
              search={search}
              setSearch={setSearch}
              setActivePage={setActivePage}
              showEmptyPage={displayEmptyPage()}
            />
            <ChatModal
              open={open}
              setOpen={setOpen}
              data={selectedUserFeedback}
              selectedConversation={selectedConversation}
              questions={core_data?.questions}
              outcomeQ={outcomeQ}
            />
          </>
        )}
        <FeedbackContainment>
          <FeedbackCentering>
            <>
              {displayedFeedback ? (
                ((filtered_data?.length || 0) > orgRule || hasCustomData) ? (
                  displayedFeedback.map((item, i) => {
                    if (item.feedback == "") return null;
                    return (
                      <Row
                        key={i}
                        onClick={() => {
                          setOpen(true);
                          setSelectedConversation(item);
                        }}
                      >
                        <BlueText>
                          {item?.outcome
                            ? getOutcomeName(item?.outcome, outcomeQ)
                            : getFactorName(item.factor, item.dimension)}{" "}
                          {feedbackType === 0 ? t('feedback') : ""}
                        </BlueText>
                        {feedbackType === 1 && <SmallText>{t('feedback')}</SmallText>}
                        <Text>{getHighlightedText(item.feedback, search)}</Text>
                        <Chips>
                          {item?.nlp?.sentiment && (
                            <Chip
                              style={{
                                backgroundColor:
                                  COLOR_MAP[item?.nlp?.sentiment]?.background,
                                color: COLOR_MAP[item?.nlp?.sentiment]?.color,
                              }}
                            >
                              {getSentimentDisplayName(item?.nlp?.sentiment)}
                            </Chip>
                          )}
                        </Chips>

                        {item.follow_up_question && (
                          <FollowUpBox>
                            <FollowUpTopSection>
                              {MiniILIcon()}
                              <FollowUpHeading>{t('aiFollowUp')}</FollowUpHeading>
                            </FollowUpTopSection>
                            <FollowUpQ>{item.follow_up_question}</FollowUpQ>
                            <FollowUpR>{item.follow_up_response}</FollowUpR>
                          </FollowUpBox>
                        )}
                        <Caret name="chevron right" />
                      </Row>
                    );
                  })
                ) : (
                  <EmptyStateContainer>

                    <Empty />
                  </EmptyStateContainer>
                )
              ) : (
                <LoadingContainer>
                    <LoadingText>{t('loading')}</LoadingText>
                </LoadingContainer>
              )}
            </>
          </FeedbackCentering>
        </FeedbackContainment>
      </Container>
      {displayedFeedback?.length > 0 && (
        <PaginationWrapper>
          <Pagination
            activePage={activePage}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
            totalPages={totalPages}
            onPageChange={changedPage}
          />
        </PaginationWrapper>
      )}
    </>
  );
};

export default Feedback;

const FeedbackIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 15L6.92474 18.1137C6.49579 18.548 6.28131 18.7652 6.09695 18.7805C5.93701 18.7938 5.78042 18.7295 5.67596 18.6076C5.55556 18.4672 5.55556 18.162 5.55556 17.5515V15.9916C5.55556 15.444 5.10707 15.0477 4.5652 14.9683C3.25374 14.7762 2.22378 13.7463 2.03168 12.4348C2 12.2186 2 11.9605 2 11.4444V6.8C2 5.11984 2 4.27976 2.32698 3.63803C2.6146 3.07354 3.07354 2.6146 3.63803 2.32698C4.27976 2 5.11984 2 6.8 2H14.2C15.8802 2 16.7202 2 17.362 2.32698C17.9265 2.6146 18.3854 3.07354 18.673 3.63803C19 4.27976 19 5.11984 19 6.8V11M19 22L16.8236 20.4869C16.5177 20.2742 16.3647 20.1678 16.1982 20.0924C16.0504 20.0255 15.8951 19.9768 15.7356 19.9474C15.5558 19.9143 15.3695 19.9143 14.9969 19.9143H13.2C12.0799 19.9143 11.5198 19.9143 11.092 19.6963C10.7157 19.5046 10.4097 19.1986 10.218 18.8223C10 18.3944 10 17.8344 10 16.7143V14.2C10 13.0799 10 12.5198 10.218 12.092C10.4097 11.7157 10.7157 11.4097 11.092 11.218C11.5198 11 12.0799 11 13.2 11H18.8C19.9201 11 20.4802 11 20.908 11.218C21.2843 11.4097 21.5903 11.7157 21.782 12.092C22 12.5198 22 13.0799 22 14.2V16.9143C22 17.8462 22 18.3121 21.8478 18.6797C21.6448 19.1697 21.2554 19.5591 20.7654 19.762C20.3978 19.9143 19.9319 19.9143 19 19.9143V22Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 32px 24px;
  background-color: white;
`;

const FollowUpR = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-left: 24px;
  margin-top: 8px;
  line-height: 1.6;
`;

const Container = styled.div`
  width: 100%;
  position: relative;
  max-height: 100%;
  padding: 0 24px 24px 24px;
  background-color: white;
  min-height: 100vh;
`;

const DropdownSection = styled.div`
  display: flex;
  margin-top: 20px;
`;

const TopTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1a1d29;
  margin: 32px 0 16px 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const TopDescription = styled.div`
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 32px;
  max-width: 800px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  position: relative;
  padding: 24px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CultureLabel = styled.div`
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #2d70e2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  margin-top: 24px;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const FollowUpTopSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const AIButton = styled.div`
  background: #2d70e2;
  font-family: "Raleway", sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background: #333;
  }
  min-width: 200px;
  max-height: 45px;
  margin-left: 20px;
`;

const DownloadButton = styled.div`
  background: #111827;
  color: white;
  padding: 10px 16px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background: #333;
  }
`;

const BottomTags = styled.div`
  display: flex;
  margin-bottom: 15px;
  font-weight: bold;
  font-size: 12px;
  color: #666d79;
`;

const IMG2 = styled.img`
  width: 20px;
  margin-left: 10px;
  margin-bottom: -5px;
`;

const TR = styled.div`
  width: ${(props) => props.width}%;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;
const Q = styled.div`
  font-weight: bold;
  margin-bottom: 20px;
  max-width: calc(100% - 200px);
`;

const Text = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: #374151;
  margin-bottom: 16px;
  padding-right: 40px;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  magin-bottom: 10px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
`;

const Description = styled.div`
  margin-bottom: 30px;
  line-height: 1.4;
`;

const FeedbackContainment = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;
`;

const FeedbackCentering = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
`;

const T2 = styled.div`
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const Score = styled.div`
  position: absolute;
  right: 0;
`;

const StyledInput = styled(Input)`
  &&& {
    width: 100%;
    margin-bottom: 24px;
    border-radius: 12px;
    background-color: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    input {
      border: none;
      background: none;
      font-size: 14px;
      color: #374151;
      padding: 12px 16px;
      &::placeholder {
        color: #9ca3af;
      }
    }

    .icon {
      margin-right: 16px;
      color: #6b7280;
    }

    &:focus-within {
      border-color: #2d70e2;
      box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
    }
  }
`;

const Caret = styled(Icon)`
  position: absolute;
  right: 24px;
  top: 24px;
  color: #9ca3af;
  transition: all 0.2s ease;
  
  ${Row}:hover & {
    color: #6b7280;
    transform: translateX(2px);
  }
`;

const Chip = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  margin-right: 8px;
  margin-bottom: 4px;
  font-weight: 600;
  border: 1px solid transparent;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 4px;
  gap: 4px;
`;

const FollowUpBox = styled.div`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  width: 100%;
  margin-right: 40px;
`;

const FollowUpQ = styled.div`
  font-size: 14px;
  color: #1a1d29;
  margin-left: 24px;
  margin-top: 8px;
  font-weight: 600;
  line-height: 1.5;
`;

const FollowUpHeading = styled.div`
  font-weight: 600;
  color: #2d70e2;
  font-size: 14px;
  line-height: 1.6;
  margin-left: 8px;
`;

const SentimentTag = styled.div`
  color: ${(props) => props.color};
  display: inline-block;
  margin-right: 5px;
  margin-left: 5px;
`;

const Tag = styled.div`
  color: #524c71;
  font-weight: 600;
  font-size: 10px;
  background-color: #f8faff;
  padding: 4px 8px;
  border-radius: 5px;
  display: inline-block;
  margin-right: 5px;
`;

const Tag2 = styled.div`
  display: inline-block;
  margin-right: 5px;
`;

const Image = styled.img`
  width: 30px;
  margin-right: 40px;
`;

const Tags = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const Action = styled.div`
  color: #2d70e2;
  font-size: 12px;
  cursor: pointer;
  margin-top: 10px;
  display: flex;
  position: absolute;
  bottom: 10px;
  right: 20px;
`;

const TableTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  margin-top: 20px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-decoration: italic;
  margin-bottom: 5px;
`;

const BlueText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1a1d29;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const SmallText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
`;

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
`;

const EmptyStateControls = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  padding: 24px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 120px;
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1200px;
  align-self: stretch;
`;

const EmptyStateDropdownContainer = styled.div`
  flex: 0 0 70%;
  min-width: 0;
  position: relative;
  z-index: 1000;
`;

const EmptyStateSearchContainer = styled.div`
  flex: 0 0 30%;
  min-width: 0;
`;

const EmptyStateSearchLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;
